/**
 * Data validation library
 */

/**
 * Validate email format
 * @param {string} email Email to validate
 * @returns {boolean} Validation result
 */
export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return regex.test(email);
};

/**
 * Validate password strength
 * @param {string} password Password to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validatePassword = (password) => {
  // Check minimum length
  if (!password || password.length < 6) {
    return { valid: false, message: 'Password must be at least 6 characters' };
  }
  
  // Check for uppercase and lowercase letters
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  // Create an array of missing requirements
  const missing = [];
  if (!hasUpperCase) missing.push('uppercase letter');
  if (!hasLowerCase) missing.push('lowercase letter');
  if (!hasNumbers) missing.push('number');
  if (!hasSpecialChars) missing.push('special character');
  
  // Check overall complexity requirements
  if (missing.length > 0) {
    const missingText = missing.join(', ');
    return { 
      valid: false, 
      message: `Password must include at least one ${missingText}`
    };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate display name
 * @param {string} displayName Display name to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim() === '') {
    return { valid: false, message: 'Name is required' };
  }
  
  if (displayName.length < 3) {
    return { valid: false, message: 'Name must be at least 3 characters' };
  }
  
  if (displayName.length > 50) {
    return { valid: false, message: 'Name must not exceed 50 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate article title
 * @param {string} title Article title to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateArticleTitle = (title) => {
  if (!title || title.trim() === '') {
    return { valid: false, message: 'Article title is required' };
  }
  
  if (title.length < 5) {
    return { valid: false, message: 'Title must be at least 5 characters' };
  }
  
  if (title.length > 200) {
    return { valid: false, message: 'Title must not exceed 200 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate article content
 * @param {string} content Article content to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateArticleContent = (content) => {
  if (!content || content.trim() === '') {
    return { valid: false, message: 'Article content is required' };
  }
  
  const textContent = content.replace(/<[^>]*>/g, ''); // Remove HTML tags
  
  if (textContent.length < 100) {
    return { valid: false, message: 'Content must be at least 100 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate comment
 * @param {string} comment Comment text to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateComment = (comment) => {
  if (!comment || comment.trim() === '') {
    return { valid: false, message: 'Comment text is required' };
  }
  
  if (comment.length < 2) {
    return { valid: false, message: 'Comment must be at least 2 characters' };
  }
  
  if (comment.length > 2000) {
    return { valid: false, message: 'Comment must not exceed 2000 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate user bio
 * @param {string} bio User bio to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateBio = (bio) => {
  if (!bio) {
    return { valid: true, message: '' }; // Bio is optional
  }
  
  if (bio.length > 1000) {
    return { valid: false, message: 'Bio must not exceed 1000 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate column title
 * @param {string} columnTitle Column title to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateColumnTitle = (columnTitle) => {
  if (!columnTitle || columnTitle.trim() === '') {
    return { valid: false, message: 'Column title is required' };
  }
  
  if (columnTitle.length < 3) {
    return { valid: false, message: 'Column title must be at least 3 characters' };
  }
  
  if (columnTitle.length > 100) {
    return { valid: false, message: 'Column title must not exceed 100 characters' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate image URL
 * @param {string} url Image URL to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateImageUrl = (url) => {
  if (!url) {
    return { valid: true, message: '' }; // Image is optional
  }
  
  // Simple URL format check
  const regex = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i;
  if (!regex.test(url)) {
    return { valid: false, message: 'Invalid image URL' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate file type and size
 * @param {File} file File to validate
 * @param {Array} allowedTypes Allowed file types
 * @param {number} maxSize Maximum file size in bytes
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateFile = (file, allowedTypes = ['image/jpeg', 'image/png', 'image/gif'], maxSize = 2 * 1024 * 1024) => {
  if (!file) {
    return { valid: false, message: 'File is required' };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, message: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` };
  }
  
  if (file.size > maxSize) {
    return { valid: false, message: `File size exceeds the limit (${Math.round(maxSize / 1024 / 1024)} MB)` };
  }
  
  return { valid: true, message: '' };
};

/**
 * Validate filename
 * @param {string} filename Filename to validate
 * @returns {{valid: boolean, message: string}} Validation result and error message if any
 */
export const validateFilename = (filename) => {
  if (!filename || filename.trim() === '') {
    return { valid: false, message: 'Filename is required' };
  }
  
  // Check that filename only contains valid characters
  const regex = /^[a-zA-Z0-9_.-]+$/;
  if (!regex.test(filename)) {
    return { 
      valid: false, 
      message: 'Filename must only contain English letters, numbers, and the following symbols: _ . -' 
    };
  }
  
  return { valid: true, message: '' };
};

// Export all validation functions
export default {
  isValidEmail,
  validatePassword,
  validateDisplayName,
  validateArticleTitle,
  validateArticleContent,
  validateComment,
  validateBio,
  validateColumnTitle,
  validateImageUrl,
  validateFile,
  validateFilename
}; 